import { ReactNode } from 'react';

interface StepShellProps {
  step: number;
  title: string;
  subtitle?: string;
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  children: ReactNode;
}

const STEP_LABELS = ['Inicio', 'Tu perfil', 'Tu piel', 'Preferencias', 'Tu perfume', 'Mouillettes', 'Resultado'];
const TOTAL_STEPS = STEP_LABELS.length;

export default function StepShell({
  step,
  title,
  subtitle,
  onBack,
  onNext,
  nextLabel = 'Continuar',
  nextDisabled = false,
  children,
}: StepShellProps) {
  const pct = Math.round((step / TOTAL_STEPS) * 100);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header centrado con logo grande */}
      <header className="relative pt-8 pb-6 px-6">
        <div className="flex flex-col items-center">
          <img
            src="/logos/elixir-full.svg"
            alt="Elixir Dubai Perfumes"
            className="h-16 w-auto select-none opacity-95"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <p className="mt-2 text-[10px] tracking-[0.45em] text-elixir-cream/45 uppercase">
            Diagnóstico Olfativo
          </p>
        </div>

        {/* Filete + paso con etiqueta */}
        <div className="flex items-center justify-center gap-4 mt-5">
          <span className="h-px w-20 bg-gradient-to-r from-transparent to-elixir-gold/55" />
          <span className="text-elixir-gold-soft text-[11px] tracking-[0.32em] uppercase">
            Paso {step} <span className="text-elixir-cream/40 mx-1">de</span> {TOTAL_STEPS}
            <span className="text-elixir-cream/40 mx-1">·</span>
            <span className="text-elixir-cream/70 normal-case tracking-normal">{STEP_LABELS[step - 1]}</span>
          </span>
          <span className="h-px w-20 bg-gradient-to-l from-transparent to-elixir-gold/55" />
        </div>

        {/* Barra de progreso visual con dots etiquetados */}
        <div className="mt-4 max-w-xl mx-auto">
          <div className="h-1.5 bg-elixir-cream/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-elixir-gold to-elixir-gold-soft transition-all duration-700 ease-out rounded-full"
              style={{ width: pct + '%' }}
            />
          </div>
          <div className="flex justify-between mt-2 px-0.5">
            {STEP_LABELS.map((label, i) => {
              const idx = i + 1;
              const done = idx < step;
              const current = idx === step;
              return (
                <div
                  key={label}
                  className={`flex flex-col items-center transition-all ${
                    current ? 'scale-110' : 'opacity-50'
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full transition-all ${
                      current
                        ? 'bg-elixir-gold ring-2 ring-elixir-gold/30 ring-offset-2 ring-offset-transparent'
                        : done
                        ? 'bg-elixir-gold/80'
                        : 'bg-elixir-cream/20'
                    }`}
                  />
                  <span
                    className={`hidden md:block text-[9px] mt-1 tracking-wider uppercase whitespace-nowrap ${
                      current ? 'text-elixir-gold' : 'text-elixir-cream/40'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </header>

      {/* Cuerpo centrado con un ancho cómodo de lectura en tablet horizontal */}
      <main className="flex-1 w-full max-w-3xl mx-auto px-8 pt-6 pb-10 animate-fade-up">
        <div className="text-center mb-8">
          <h1 className="display text-4xl md:text-5xl text-elixir-cream">{title}</h1>
          {subtitle && (
            <p className="serif italic text-elixir-cream/65 mt-3 text-lg max-w-xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
        {children}
      </main>

      {/* Footer con botones bien centrado */}
      <footer className="px-8 pb-10 pt-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <button onClick={onBack} className="btn-ghost" disabled={!onBack}>
            ← Atrás
          </button>
          <button onClick={onNext} className="btn-primary" disabled={nextDisabled || !onNext}>
            {nextLabel}
          </button>
        </div>
      </footer>
    </div>
  );
}
